import { useState } from "react";
import type { MarketplaceAccount, MarketplaceChannel } from "../../../types/marketplaceAccount";
import { disconnectMarketplaceConnection, openMarketplaceReconnect } from "../../../services/marketplaceConnectionService";
import { supportsMarketplaceChannelControls } from "../../../utils/marketplaceConnectionStatus";

type Props = {
  account: MarketplaceAccount;
  channel: MarketplaceChannel;
  onChanged: () => void;
  showDisconnect?: boolean;
};

export function MarketplaceChannelControls({ account, channel, onChanged, showDisconnect = false }: Props) {
  const [busy, setBusy] = useState(false);
  const available = supportsMarketplaceChannelControls(account);
  const title = `${channel === "erp" ? "ERP" : "Chat"} connection`;

  const run = async (action: () => Promise<unknown>, refreshAfter = true) => {
    setBusy(true);
    try {
      await action();
      if (refreshAfter) onChanged();
    } catch (error) {
      window.alert(error instanceof Error ? error.message : `${title} action failed`);
    } finally {
      setBusy(false);
    }
  };

  if (!available) return <span className="tw-text-xs tw-font-medium tw-text-slate-400" title="This marketplace has no separate ERP or Chat authorization endpoint">Unavailable</span>;

  return (
    <div className="tw-flex tw-items-center tw-gap-1">
      <button type="button" title={`Reconnect ${title}`} aria-label={`Reconnect ${title}`} disabled={busy} onClick={() => void run(() => openMarketplaceReconnect(account, channel), false)} className="tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-md tw-border tw-border-slate-200 tw-text-slate-600 hover:tw-bg-slate-100 disabled:tw-opacity-40 dark:tw-border-slate-700 dark:tw-text-slate-300 dark:hover:tw-bg-slate-800"><span className="material-symbols-outlined tw-text-base">sync</span></button>
      {showDisconnect && <button type="button" title={`Disconnect ${title}`} aria-label={`Disconnect ${title}`} disabled={busy} onClick={() => { if (window.confirm(`Disconnect ${title}? The other connection stays active.`)) void run(() => disconnectMarketplaceConnection(account, channel)); }} className="tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-md tw-border tw-border-red-200 tw-text-red-600 hover:tw-bg-red-50 disabled:tw-opacity-40 dark:tw-border-red-900 dark:hover:tw-bg-red-950/30"><span className="material-symbols-outlined tw-text-base">link_off</span></button>}
    </div>
  );
}
